import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { shippedText } from '../../shared/ShippedNotice';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import ShipConfirmDialog from './ShipConfirmDialog';
import PackagesPanel from './PackagesPanel';
import ShippedPanel from './ShippedPanel';
import { shippablePackages, openPackageOf, holdsAnything } from './packageShared';
import PickSlipReportView from './PickSlipReportView';
import BoxLabelView from './BoxLabelView';
import SenderLabelsView from './SenderLabelsView';
import PackingListReportView from './PackingListReportView';
import { usePackingStation } from './packingStation';
import LineOrderSwitch, { inLineOrder } from '../../shared/LineOrderSwitch';
import { guideFor, glowClass, FulfilmentStepper, packNowFor, NowPanel } from './fulfilmentGuide';
import ReportOverlay from '../../shared/ReportOverlay';
import RecordChip from '../../shared/RecordChip';
import { CategoryPill } from '../../shared/Pill';
import { formatNumber } from '../../shared/numberFormat';
import { useScreenAccess } from '../../shared/PermissionsContext';

// Restricting to the barcode formats actually in use (Optim's own printed
// SO barcode is Data Matrix — see std-technology-stack.md's Barcode
// Standard; Product UoM barcodes are typically retail UPC/EAN, sometimes
// Code128, sometimes a manufacturer-applied Data Matrix on the product's
// own packaging; a package label's code is QR, issue #445, so any phone
// camera opens it) means every frame decode attempt isn't also wasted
// testing PDF417/Aztec/etc that will never appear here — fewer wasted
// cycles per frame, faster and more reliable at picking up the barcode
// that's actually in view. Code128 stays in the list even though Optim no
// longer prints it — Product UoM barcodes already recorded against it
// don't change format retroactively.
const BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
];

// Pick, Pack and Ship Order — one narrow, phone-first page for each stage
// (issue #306): scan (or type) an order's printed barcode (see OrderBarcode
// in PickSlipReportView.js) or choose it from the orders waiting at that
// stage, then scan each Product UoM's own barcode (ProductUomPage.js) to
// Pick, Pack or Put Back against that line (see vis_order_fulfilment.md's
// Picking and Packing). Works the same for every order type (issue #298): everything it
// reads and does goes through /api/fulfilment, so the order arrives in one
// shared shape (number, type, who it's for, lines) whatever kind it is.
// Deliberately its own page rather than reusing SalesOrderPage.js, which is
// a wide desktop edit form.
const SCANNER_ELEMENT_ID = 'pick-order-scanner';
// Presence-based suppression (via html5-qrcode's per-frame "nothing
// decoded" callback) turned out to fire far more often/unpredictably than
// expected and broke scanning outright — reverted to a short time-based
// debounce instead. Originally 1.5s, cut to 600ms so back-to-back
// identical-barcode packs weren't swallowed, then found too short in
// practice -- just holding the camera steady over one pack while finding
// the next item could re-decode and count it twice. 4000ms matches the
// real physical motion of picking up a pack and moving the camera to it,
// with a further margin on top -- deliberately erring slower rather than
// risk a hover double-counting one item. The vibrate/beep confirmation
// (see signalScan) is the other half of this -- it lets a picker feel/hear
// a scan land and move on, rather than lingering on the barcode uncertain
// whether it registered.
const RESCAN_SUPPRESS_MS = 4000;

// How long the scan-result banner stays up -- deliberately not the same
// value for every outcome. A success is a glance-and-move-on: the
// picker already felt/heard it land (see signalScan), the banner is just
// belt-and-braces. A warn/error is different -- it caught the picker out,
// and they need actual time to read it, understand what went wrong, and
// decide what to do next, not have it vanish mid-read.
const OK_FEEDBACK_MS = 3000;
const ALERT_FEEDBACK_MS = 6000;

// Same breakdown PickSlipReportView.js's own breakdownForBaseQty()
// computes for the print sheet — an arbitrary base-unit quantity broken
// into the largest whole sellable units first (e.g. 10 Packs of 10 = 100
// PCS -> "1 BX" if a Box of 100 exists), since there's no such thing as
// half a Box to physically grab. Shared by the ordered-qty breakdown (a
// display hint for which unit to reach for) and the Picked/Packed
// breakdowns below — each breaks down its *own* quantity independently,
// e.g. a partly-picked line's Picked breakdown has no reason to match the
// ordered one row-for-row.
function breakdownForBaseQty(line, baseQtyTotal) {
  const allUoms = line.product?.uoms || [];
  const soldUom = allUoms.find((pu) => String(pu.uom_id) === String(line.uom_id));
  const soldRate = soldUom ? Number(soldUom.conversion_rate) : 1;

  const candidates = allUoms
    .filter((pu) => pu.can_sell && Number(pu.conversion_rate) >= soldRate)
    .sort((a, b) => Number(b.conversion_rate) - Number(a.conversion_rate));

  const rows = [];
  let remaining = baseQtyTotal;
  candidates.forEach((pu) => {
    const rate = Number(pu.conversion_rate);
    const whole = Math.floor(remaining / rate);
    if (whole > 0) {
      rows.push({ uomName: pu.uom?.code || pu.uom?.name, qty: whole });
      remaining -= whole * rate;
    }
  });
  if (rows.length === 0) {
    rows.push({ uomName: line.uom?.code || line.uom?.name, qty: baseQtyTotal / soldRate });
  }
  return rows;
}

// Physical confirmation for the camera scan flow -- scans register quickly
// enough back-to-back that staff sometimes fire a second one on the same
// item before realising the first already counted, without ever looking
// away from positioning the barcode. A short buzz per scan (double buzz
// when it didn't count, e.g. already fully Picked) lets a picker feel each
// scan land by touch, encouraging a natural pause before the next one --
// without touching the scan-speed/debounce tuning itself, which already
// trades off against swallowing legitimate fast repeat scans of the same
// multi-pack line. No-op on devices/browsers without the Vibration API
// (notably iOS Safari).
function vibrateFor(status) {
  if (!navigator.vibrate) return;
  navigator.vibrate(status === 'ok' ? 40 : [40, 60, 40]);
}

// One short high beep for a scan that counted, two short low beeps for one
// that didn't -- audible confirmation alongside vibrateFor for a noisy
// warehouse floor or a device with vibration muted. Needs an AudioContext
// created (or resumed) from within an actual user gesture or mobile
// browsers silently refuse to play it -- see ensureAudioContext, called
// from the Scan button's own onClick. That only resumes it once though --
// mobile browsers can auto-suspend an idle AudioContext again mid-session,
// which would otherwise fail silently (no error, no sound) on every beep
// after that point, so this defensively re-resumes it on every call too.
function beepFor(ctx, status) {
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const playTone = (startAt, freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.value = 0.15;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + 0.09);
  };
  const now = ctx.currentTime;
  if (status === 'ok') {
    playTone(now, 1200);
  } else {
    playTone(now, 480);
    playTone(now + 0.13, 480);
  }
}

function formatQty(n) {
  return formatNumber(Number(n || 0), { maxDecimals: 2 });
}

function soldRateFor(line) {
  const soldUom = (line.product?.uoms || []).find((pu) => String(pu.uom_id) === String(line.uom_id));
  return soldUom ? Number(soldUom.conversion_rate) : 1;
}

// How much base-unit quantity a tap of `action` can actually still move on
// this line — mirrors the scan's own caps (fulfilmentService.scan) so a
// tap button never offers more than a real scan of that unit would apply.
// Pick: capped by what's left to reach ordered qty. Pack: capped by what's
// Picked but not yet Packed -- can't Pack ahead of Picked. Put Back: capped by what's
// currently Picked, since that's all there is to reverse. Take Out: capped
// by what's in the open package (boxQty).
function availableBaseQtyForAction(line, action, boxQty = 0) {
  const soldRate = soldRateFor(line);
  const qty = Number(line.qty || 0);
  const qtyPicked = Number(line.qty_picked || 0);
  const qtyPacked = Number(line.qty_packed || 0);
  let sold;
  if (action === 'pack') sold = Math.max(0, Math.min(qty, qtyPicked) - qtyPacked);
  else if (action === 'put') sold = qtyPicked;
  else if (action === 'unpack') sold = boxQty;
  else sold = Math.max(0, qty - qtyPicked);
  return sold * soldRate;
}

// Same candidate set breakdownForBaseQty() greedily fills from (every
// sellable UoM at least as large as the line's own sold UoM) — exposed on
// its own, with each ProductUom's id, so a line's card can offer a "+1 <unit>"
// tap for each one as a no-scan alternative to the camera. Largest first,
// same reading order as the ordered-qty breakdown above it. Filtered to
// units that still fit within what's left to move for `action` -- offering
// "+1 BX" when only 1 PK remains lets a tap silently over-apply (the server
// clamps it, so no bad data results, but the button promised more than the
// order actually needs).
function tapCandidatesFor(line, action, boxQty = 0) {
  const allUoms = line.product?.uoms || [];
  const soldRate = soldRateFor(line);
  const availableBase = availableBaseQtyForAction(line, action, boxQty);
  return allUoms
    .filter((pu) => pu.can_sell && Number(pu.conversion_rate) >= soldRate && Number(pu.conversion_rate) <= availableBase)
    .sort((a, b) => Number(b.conversion_rate) - Number(a.conversion_rate))
    .map((pu) => ({ productUomId: pu.id, uomName: pu.uom?.code || pu.uom?.name }));
}

function pickingBreakdown(line) {
  return breakdownForBaseQty(line, Number(line.qty || 0) * soldRateFor(line));
}
function pickedBreakdown(line) {
  return breakdownForBaseQty(line, Number(line.qty_picked || 0) * soldRateFor(line));
}
function packedBreakdown(line) {
  return breakdownForBaseQty(line, Number(line.qty_packed || 0) * soldRateFor(line));
}
function shippedBreakdown(line) {
  return breakdownForBaseQty(line, Number(line.qty_shipped || 0) * soldRateFor(line));
}
// What's left to Pick or Pack on this line — feeds the "scan next" prompt.
// Never negative; a line can't have more Picked/Packed than ordered
// (the scan caps it server-side already).
function outstandingQty(line, field) {
  return Math.max(0, Number(line.qty || 0) - Number(line[field] || 0));
}
function remainingBreakdown(line, field) {
  return breakdownForBaseQty(line, outstandingQty(line, field) * soldRateFor(line));
}

// A SKU card's status chip: what's left to do on the line at this page's
// stage, in its own units ("3 PCK to pick"), or that it's done, or that it's
// waiting on the stage before. tone: 'todo' | 'done' | 'waiting'.
const LINE_STATUS_TONES = {
  todo: { background: '#fff4e0', color: '#9a6700' },
  done: { background: '#e6f4ea', color: '#1e7e34' },
  waiting: { background: '#eceef1', color: '#5a6b85' },
};

function lineStatusFor(line, mode) {
  const qty = Number(line.qty || 0);
  const picked = Number(line.qty_picked || 0);
  const packed = Number(line.qty_packed || 0);
  const shipped = Number(line.qty_shipped || 0);
  const amount = (q) => breakdownForBaseQty(line, q * soldRateFor(line)).map((r) => `${formatQty(r.qty)} ${r.uomName}`).join(' + ');
  if (mode === 'pack') {
    if (picked > packed) return { tone: 'todo', text: `${amount(picked - packed)} to pack` };
    return packed >= qty ? { tone: 'done', text: '✓ Fully Packed' } : { tone: 'waiting', text: 'Waiting to be picked' };
  }
  if (mode === 'ship') {
    if (packed > shipped) return { tone: 'todo', text: `${amount(packed - shipped)} to ship` };
    return shipped >= qty ? { tone: 'done', text: '✓ Fully Shipped' } : { tone: 'waiting', text: 'Waiting to be packed' };
  }
  return picked < qty ? { tone: 'todo', text: `${amount(qty - picked)} to pick` } : { tone: 'done', text: '✓ Fully Picked' };
}

// Put Back reverses a pick (debits qty_picked) — a correction action,
// not a fourth quantity of its own. See fulfilmentService.scan.
// Deliberately not called "Return" -- that word's taken by Customer
// Returns/RMA, a different (likely future) concept on the same Sales
// Order domain; "Put Away" was also considered and dropped for the
// opposite reason, since it already means something specific in
// warehouse/WMS terms (moving newly received stock into storage).
// Each stage's page offers only its own scans (issue #306): Pick offers
// Pick and Put Back (un-pick, back to stock); Pack, at a packing station
// only (issue #305, packingStation.js), offers Pack and Take Out of Package
// (un-pack: it stays picked, ready to pack again). Pack away from a
// packing station, and Ship Order, have no scans at all. Ship is
// NOT one of these per-line scan actions (issue #205) — items are sealed
// inside a package by the time they ship, so it's an order-level action of its
// own (see the Ship button below), not a barcode scan against one line.
const PICK = { key: 'pick', label: 'Pick', scanLabel: 'Scan to Pick' };
const PACK = { key: 'pack', label: 'Pack', scanLabel: 'Scan to Pack' };
const PUT_BACK = { key: 'put', label: 'Put Back', scanLabel: 'Scan to Put Back' };
const TAKE_OUT = { key: 'unpack', label: 'Take Out', scanLabel: 'Scan to Take Out of Package' };
// Put Back and Take Out are corrections, not progress towards a target.
const isCorrection = (action) => action === 'put' || action === 'unpack';

function actionsFor(mode, atPackingStation) {
  if (mode === 'pick') return [PICK, PUT_BACK];
  if (mode === 'pack' && atPackingStation) return [PACK, TAKE_OUT];
  return [];
}

// Each stage's page: its heading, its list's heading, and what each
// listed order shows.
const STAGE_PAGES = {
  pick: { title: 'Pick', listTitle: 'Orders to pick', progress: (o) => `${formatQty(o.qty_picked_total)} of ${formatQty(o.qty_total)} Picked` },
  pack: { title: 'Pack', listTitle: 'Orders to pack', progress: (o) => `${formatQty(o.qty_packed_total)} of ${formatQty(o.qty_picked_total)} Packed` },
  ship: { title: 'Ship Order', listTitle: 'Orders to ship', progress: (o) => `${formatQty(o.qty_shipped_total)} of ${formatQty(o.qty_total)} Shipped` },
};

// A stage's word for a SKU that's all the way through it.
const FULLY_DONE = { pick: 'fully picked', pack: 'fully packed', ship: 'fully shipped' };

// What a stage's list says when nothing is waiting at it.
const NOTHING_READY = { pick: 'Nothing to pick', pack: 'Nothing ready to pack', ship: 'Nothing ready to ship' };

// Each stage's own page (issue #306). The stepper's links and Proceed to Ship Now
// open the same order on the next stage's page.
const STAGE_PATHS = { pick: '/warehouse-pick', pack: '/warehouse-pack', ship: '/warehouse-ship' };

// mode: which stage this page is — 'pick' (Warehouse › Pick), 'pack'
// (Warehouse › Pack) or 'ship' (Warehouse › Ship Order).
function PickOrderPage({ mode = 'pick' }) {
  const stagePage = STAGE_PAGES[mode] || STAGE_PAGES.pick;
  const { canUpdate } = useScreenAccess();
  const [scanMode, setScanMode] = useState(null); // null | 'order' | 'action'
  const navigate = useNavigate();
  const { isStation } = usePackingStation();
  const ACTIONS = actionsFor(mode, isStation);
  const [action, setAction] = useState(ACTIONS[0]?.key ?? null);
  const [manualValue, setManualValue] = useState('');
  const [order, setOrder] = useState(null);
  // SKU order every time an order opens; Line sequence lasts for this
  // order's view only (see LineOrderSwitch.js).
  const [lineOrder, setLineOrder] = useState('sku');
  useEffect(() => {
    setLineOrder('sku');
    setShowOtherLines(false);
    setAction(ACTIONS[0]?.key ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.order_type, order?.id]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null); // { status: 'ok'|'warn'|'error', message }
  const [openOrders, setOpenOrders] = useState([]);
  // null until this stage's list has loaded. When it's empty, the orders
  // waiting at the stages before it, so the page shows where the work is
  // instead of a blank list.
  const [openLoaded, setOpenLoaded] = useState(false);
  const [earlierOrders, setEarlierOrders] = useState([]);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [showPickSlip, setShowPickSlip] = useState(false);
  // The Packing List open (issues #296, #445): { shipmentId } to reprint a
  // shipment's, or { orderType, orderId } for the packages not yet shipped.
  // packingListPrintedFor: the order and packages it was last opened for.
  const [packingList, setPackingList] = useState(null);
  const [packingListPrintedFor, setPackingListPrintedFor] = useState('');
  // The package whose label preview is open (issue #300), and the
  // package a scanned label named, marked in the Packages list.
  const [labelPackageId, setLabelPackageId] = useState(null);
  const [senderLabelsShipmentId, setSenderLabelsShipmentId] = useState(null);
  const [highlightPackageId, setHighlightPackageId] = useState(null);
  // Pack's Now panel: lines with nothing to do folded away until opened,
  // and a package change under way from the panel's buttons.
  const [showOtherLines, setShowOtherLines] = useState(false);
  const [packBusy, setPackBusy] = useState(false);
  const lastScanRef = useRef({ text: '', time: 0 });
  // Pack has no Pack scan away from a packing station.
  useEffect(() => {
    if (!ACTIONS.some((a) => a.key === action)) setAction(ACTIONS[0]?.key ?? null);
  }, [ACTIONS, action]);

  // The scanner effect below only restarts the camera when scanMode
  // changes, not when action does -- switching Pick/Pack/Put Back mid-scan
  // (without tapping Stop Scanning) doesn't remount it. Its decode callback
  // and applyScanResult/doScan therefore need action's *current* value at
  // scan time via this ref, not the value closed over when the camera
  // started -- otherwise a mode switch mid-scan keeps acting on the old
  // mode (e.g. switching to Put Back after a line is fully Picked kept
  // reporting "Already fully Picked" instead of putting it back).
  const actionRef = useRef(action);
  useEffect(() => { actionRef.current = action; }, [action]);
  // Same reason: the scan says whether it comes from a packing station.
  const stationRef = useRef(isStation);
  useEffect(() => { stationRef.current = isStation; }, [isStation]);

  // Lazily created/resumed from the Scan button's own onClick (a genuine
  // user gesture) -- mobile browsers block audio started any other way.
  // Reused for every beep afterwards, including from async scan results.
  const audioCtxRef = useRef(null);
  const ensureAudioContext = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audioCtxRef.current = new Ctx();
    }
    if (audioCtxRef.current?.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
  };
  const signalScan = (status) => {
    vibrateFor(status);
    beepFor(audioCtxRef.current, status);
  };

  // Scanning continues in the background regardless -- this just stops a
  // stale message from sitting on screen looking like it's blocking the
  // next scan. Duration depends on which kind of message it is (see
  // OK_FEEDBACK_MS/ALERT_FEEDBACK_MS above).
  useEffect(() => {
    if (!scanFeedback) return undefined;
    const ms = scanFeedback.status === 'ok' ? OK_FEEDBACK_MS : ALERT_FEEDBACK_MS;
    const timer = setTimeout(() => setScanFeedback(null), ms);
    return () => clearTimeout(timer);
  }, [scanFeedback]);

  // The paper printout is a backup, not the only way to start (see
  // vis_order_fulfilment.md) -- this list is the orders waiting at this
  // page's stage (issue #306), refreshed whenever we land back on it.
  useEffect(() => {
    if (order) return;
    setOpenLoaded(false);
    axios.get('/api/fulfilment/open', { params: { limit: 20, stage: mode } })
      .then((res) => setOpenOrders(res.data.data || []))
      .catch(() => setOpenOrders([]))
      .finally(() => setOpenLoaded(true));
  }, [order, mode]);

  // Nothing waiting here: list the orders at the earlier stages, latest
  // stage first, each order once (an order can wait at Pick and Pack at
  // once — it shows under Pack, the further along).
  useEffect(() => {
    if (order || !openLoaded || openOrders.length > 0 || mode === 'pick') {
      setEarlierOrders([]);
      return;
    }
    const stages = mode === 'ship' ? ['pack', 'pick'] : ['pick'];
    Promise.all(stages.map((stage) => axios.get('/api/fulfilment/open', { params: { limit: 20, stage } })
      .then((res) => ({ stage, orders: res.data.data || [] }))
      .catch(() => ({ stage, orders: [] }))))
      .then((groups) => {
        const seen = new Set();
        setEarlierOrders(groups.map(({ stage, orders }) => ({
          stage,
          orders: orders.filter((o) => {
            const key = `${o.order_type}-${o.id}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }),
        })).filter((g) => g.orders.length > 0));
      });
  }, [order, openLoaded, openOrders, mode]);

  // openShip: Proceed to Ship Now (issue #306) lands here with the ship
  // confirmation to open once the order's loaded.
  const selectOrder = async (orderType, id, { openShip = false } = {}) => {
    setLoading(true);
    setError('');
    setHighlightPackageId(null);
    try {
      const orderRes = await axios.get(`/api/fulfilment/${orderType}/${id}`);
      setOrder(orderRes.data.data);
      if (openShip && !orderRes.data.data.ship_blocked && shippablePackages(orderRes.data.data.packages).length > 0) setShipDialogOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load that Order');
    } finally {
      setLoading(false);
    }
  };

  // Deep link from an order's own "Pick / Pack" action (e.g.
  // SalesOrderPage.js's Actions menu) — skips re-scanning/re-typing an Order already open on a desktop
  // screen. Router state, not a query string, matching this app's existing
  // "already-loaded record handed through navigate(path, { state })" shape
  // (e.g. QuotePage.js's Customer preload) rather than a URL param this page
  // would otherwise need to parse itself.
  // A Parcel or Package label opened by staff on their phone (BoxScanPage.js) lands
  // here with ?box=<its label link token>, and opens that package's order.
  // The stepper's links, Proceed to Ship Now and the Fulfilments list hand the
  // order over the same way (issue #306).
  const location = useLocation();
  useEffect(() => {
    const box = new URLSearchParams(location.search).get('box');
    if (box) lookup(box);
    else if (location.state?.orderId) selectOrder(location.state.orderType || 'sales_order', location.state.orderId, { openShip: !!location.state.openShip });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Covers both the camera scan of the order's own barcode and the manual
  // "Find" text entry -- same physical confirmation as scanning a line
  // item (see signalScan), which this lookup previously had none of at
  // all (it doesn't go through applyScanResult, item scans' own path).
  // A scanned Parcel or Package label (issue #300) opens the order its package is
  // on, with that package marked.
  const lookup = async (displayNumber) => {
    setLoading(true);
    setError('');
    try {
      const lookupRes = await axios.get('/api/fulfilment/lookup', { params: { code: displayNumber } });
      const { order_type: orderType, id, package_id: packageId } = lookupRes.data.data;
      const orderRes = await axios.get(`/api/fulfilment/${orderType}/${id}`);
      setOrder(orderRes.data.data);
      setHighlightPackageId(packageId || null);
      signalScan('ok');
    } catch (err) {
      setOrder(null);
      setHighlightPackageId(null);
      setError(err.response?.data?.message || `"${displayNumber}" not found`);
      signalScan('error');
    } finally {
      setLoading(false);
    }
  };

  // Ship (issue #205) — order-level, the same Ship pass as the order's own
  // page (e.g. SalesOrderPage.js's handleConfirmShip). Ships one or more
  // outermost packages (issues #299, #313), with the confirmation dialog
  // previewing what's in them first (see ShipConfirmSummary.js).
  const canShipPackage = shippablePackages(order?.packages).length > 0;
  // Says so on the greyed Ship button once everything has gone out.
  const fullyShipped = (order?.lines || []).some((l) => Number(l.qty || 0) - Number(l.qty_cancelled || 0) > 0)
    && (order?.lines || []).every((l) => Number(l.qty_shipped || 0) >= Number(l.qty || 0) - Number(l.qty_cancelled || 0));

  const handleConfirmShip = async (payload) => {
    setShipping(true);
    try {
      const res = await axios.post(`/api/fulfilment/${order.order_type}/${order.id}/ship`, payload);
      setOrder(res.data.data);
      setShipDialogOpen(false);
      // The new shipment and, when Ship invoiced it, its Invoice (issue #269).
      setScanFeedback({ status: 'ok', message: shippedText(res.data.shipment) });
    } catch (err) {
      setScanFeedback({ status: 'error', message: err.response?.data?.message || `Failed to ship ${order.type_label}` });
      signalScan('error');
    }
    setShipping(false);
  };

  const applyScanResult = (res) => {
    const { product, qty, qty_picked, qty_packed, qty_shipped, fully_picked, applied, blocked_reason, on_hold, on_hold_reason, packages } = res.data.data;
    setOrder((prev) => ({
      ...prev,
      on_hold,
      on_hold_reason,
      packages,
      lines: prev.lines.map((l) => (l.product?.sku === product.sku ? { ...l, qty_picked, qty_packed, qty_shipped } : l)),
    }));

    // A scan that hit the cap (already fully Picked/Packed/Shipped, or
    // blocked by the step before it, or nothing Picked left to Put Back)
    // still comes back success:true from the server — applied:false is
    // how it's told apart from one that actually moved a quantity, so it
    // doesn't get shown as if it did. blocked_reason distinguishes "capped
    // by the qty ordered" from "capped by the step before this one" (e.g.
    // Pack can't get ahead of Pick) — a different problem needing a
    // different message so staff knows what to actually do next.
    const scanAction = actionRef.current;
    if (applied === false) {
      let already;
      if (blocked_reason === 'in_closed_package') {
        // Put Back only reaches the open package (issue #299) — a closed
        // package's contents stay as they were when it was closed.
        already = scanAction === 'unpack' ? 'In a closed package — reopen it to take it out' : 'In a closed package — reopen it to Put Back';
      } else if (blocked_reason === 'packed') {
        // Away from a packing station, Put Back reaches only what's been
        // picked and not yet packed (issue #305).
        already = 'Already packed — Put Back at a packing station';
      } else if (blocked_reason === 'not_in_package') {
        already = 'Not in the open package';
      } else if (scanAction === 'put') {
        already = 'Nothing Picked to Put Back';
      } else if (blocked_reason === 'pick_first') {
        already = 'Not yet Picked';
      } else if (blocked_reason === 'pack_first') {
        already = 'Not yet Packed';
      } else {
        already = scanAction === 'pack' ? 'Already fully Packed' : 'Already fully Picked';
      }
      setScanFeedback({ status: 'warn', message: `${product.sku} — ${already}` });
      signalScan('warn');
      return;
    }

    const matchedLine = order.lines.find((l) => l.product?.sku === product.sku);

    // Put Back is a one-off correction, not progress toward a target the
    // way Pick/Pack are (see remainingLines/nextBannerEl above, which
    // already skip it for the same reason) -- so unlike them, it shows
    // what just moved (the delta), not a running total against qty. A
    // "1 of 3 Picked" total after a Put Back reads like a pick just
    // happened, the opposite of what occurred. Same whole-unit breakdown
    // shape the line cards use (e.g. "1 PK"), sized to the delta only --
    // in practice always small, since one scan/tap corrects by one unit's
    // worth.
    if (isCorrection(scanAction)) {
      const deltaQty = scanAction === 'put'
        ? Math.max(0, Number(matchedLine?.qty_picked || 0) - qty_picked)
        : Math.max(0, Number(matchedLine?.qty_packed || 0) - qty_packed);
      const deltaRows = matchedLine ? breakdownForBaseQty(matchedLine, deltaQty * soldRateFor(matchedLine)) : [];
      const deltaText = deltaRows.length
        ? deltaRows.map((r) => `${formatQty(r.qty)} ${r.uomName}`).join(' + ')
        : formatQty(deltaQty);
      setScanFeedback({ status: 'ok', message: `${product.sku} — ${deltaText} ${scanAction === 'put' ? 'Put Back' : 'taken out of the package'}` });
      signalScan('ok');
      return;
    }

    // Plain "picked of ordered UoM" in the line's own sold UoM, not the
    // mixed multi-unit breakdown the line cards show (e.g. "1 BX + 2 PK")
    // -- that reads well as a running total but is too long for a banner
    // that has to be read in passing, and the ratio alone already answers
    // "how much is left" without spelling it out. "of", not "/" -- "1/5"
    // reads as the fraction one-fifth at a glance, not "1 of 5 units".
    // UoM code comes from the line already held in state, not the scan
    // response, which doesn't carry it.
    const uom = matchedLine?.uom?.code || matchedLine?.uom?.name || '';
    const ratio = (n) => `${formatQty(n)} of ${formatQty(qty)}${uom ? ` ${uom}` : ''}`;
    // Which package it went into, once there's more than one to tell apart
    // (issue #299).
    const openPkg = openPackageOf(packages);
    const intoBox = openPkg && packages.length > 1 ? ` → ${openPkg.name}` : '';
    const detail = scanAction === 'pack' ? `${ratio(qty_packed)} Packed${intoBox}` : `${ratio(qty_picked)} Picked`;
    setScanFeedback({ status: 'ok', message: `${fully_picked ? '✓ ' : ''}${product.sku} ${detail}` });
    signalScan('ok');
  };

  const doScan = async (barcode) => {
    try {
      const res = await axios.post(`/api/fulfilment/${order.order_type}/${order.id}/scan`, { barcode, action: actionRef.current, packing: stationRef.current });
      applyScanResult(res);
    } catch (err) {
      setScanFeedback({ status: 'error', message: err.response?.data?.message || 'Barcode not recognised' });
      signalScan('error');
    }
  };

  // Tap-instead-of-scan fallback — same effect as scanning that ProductUom's
  // own barcode, just skipping the camera round-trip (paper/scanning is a
  // backup per vis_order_fulfilment.md, not the only way to work a line).
  const doTap = async (productUomId) => {
    try {
      const res = await axios.post(`/api/fulfilment/${order.order_type}/${order.id}/scan`, { product_uom_id: productUomId, action, packing: isStation });
      applyScanResult(res);
    } catch (err) {
      setScanFeedback({ status: 'error', message: err.response?.data?.message || 'Could not update' });
      signalScan('error');
    }
  };

  useEffect(() => {
    if (!scanMode) return undefined;
    const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
      formatsToSupport: BARCODE_FORMATS,
      // Uses the browser's own native BarcodeDetector where the device
      // supports it (most current Android Chrome) instead of the JS-only
      // decoder — meaningfully faster/more reliable, falls back to the
      // JS decoder automatically wherever it isn't available.
      useBarCodeDetectorIfSupported: true,
    });
    // Action mode's scanner container sits much further down a longer page
    // (order card, action selector, suggestion banner, button all above
    // it) than order mode's near-empty one — starting on the same tick the
    // container mounts risked html5-qrcode measuring it before layout had
    // actually settled, silently dropping the corner-marker locator
    // overlay in that mode specifically. Double rAF waits for the browser
    // to have painted the current layout first.
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scanner.start(
          { facingMode: 'environment' },
          // aspectRatio forces the video preview itself into a wide/short
          // shape (rather than the camera's often-tall native ratio) —
          // html5-qrcode sizes its own overlay/decode-region elements
          // consistently with this option. Don't also fight it with a CSS
          // maxHeight/overflow:hidden on the container: that clipped the
          // library's own corner-marker overlay (only the bottom two
          // survived, deformed) and likely misaligned its decode region too.
          // Same qrbox for both modes — a smaller box was tried for the SKU/
          // Product scan on the theory that a tighter target suits a smaller
          // real-world barcode, but the Order scan (260x120) measurably
          // works better in practice, so both now use it.
          // Tried 2.2 for a shorter frame (more screen visible around the
          // camera) — even that small a step measurably hurt recognition
          // ("Cannot recognise barcode" became common), so back to 2.0.
          // Reclaim screen space by shrinking what's *around* the camera
          // instead (see the scanMode === 'action' render below), not the
          // camera's own decode geometry.
          { fps: 10, qrbox: { width: 260, height: 120 }, aspectRatio: 2.0 },
          (decodedText) => {
            const now = Date.now();
            if (decodedText === lastScanRef.current.text && now - lastScanRef.current.time < RESCAN_SUPPRESS_MS) return;
            lastScanRef.current = { text: decodedText, time: now };

            if (scanMode === 'order') {
              // Don't stop() here too — setScanMode(null) below unmounts
              // this effect, whose own cleanup (right below) stops the
              // scanner once. Calling stop() twice throws synchronously in
              // html5-qrcode rather than rejecting, which crashes the whole
              // page (uncaught error during React's effect cleanup shows as
              // a blank screen).
              setScanMode(null);
              lookup(decodedText);
            } else {
              // Action mode stays open — staff scan several packs in a row.
              doScan(decodedText);
            }
          },
          () => {},
        ).catch((err) => setError(err.message || 'Could not start camera'));
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      try {
        scanner.stop().catch(() => {});
      } catch {
        // already stopped/never started — ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode]);

  // Keeps the phone's screen from dimming/locking mid-scan-session --
  // without it, the OS's normal idle timeout applies just like on any
  // other screen, forcing staff to unlock and reopen the camera between
  // scans. The Wake Lock API is the sanctioned browser mechanism for
  // this, not a workaround -- it's a real permission request, silently a
  // no-op on browsers that don't support it (still-common on iOS Safari
  // versions), and it's the browser itself that releases the lock the
  // moment the tab is backgrounded, so it has to be re-requested on
  // visibilitychange too, not just once when scanning starts.
  useEffect(() => {
    if (!scanMode || !('wakeLock' in navigator)) return undefined;
    let sentinel = null;
    const acquire = async () => {
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        // Denied/unsupported in this state (e.g. backgrounded, low
        // battery) -- scanning still works, the screen just dims on its
        // own schedule.
      }
    };
    acquire();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquire();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      sentinel?.release().catch(() => {});
    };
  }, [scanMode]);

  const reset = () => {
    setOrder(null);
    setHighlightPackageId(null);
    setError('');
    setManualValue('');
    setScanFeedback(null);
    setScanMode(null);
  };

  // Handing the order on to the next stage (issue #306) — always staff's
  // choice, never automatic: the stepper's Pick / Pack / Ship links, or
  // Proceed to Ship Now in Pack's Now panel (which opens the ship
  // confirmation straight away). Scan Another Order goes back to the list.
  const continueTo = (stage) => navigate(STAGE_PATHS[stage], { state: { orderType: order.order_type, orderId: order.id, openShip: stage === 'ship' } });

  const currentAction = ACTIONS.find((a) => a.key === action);
  // Not yet open to picking (a Sales Order before Accept) — the order type
  // says why (not_ready).
  const notReady = !!order && !order.can_fulfil;

  // The follow-the-ball guide (fulfilmentGuide.js): what to do next and the
  // one button to glow. A suggestion only — every button still works as
  // before. Only for staff who can change the order, on an order open to
  // fulfilment.
  const guide = order && canUpdate && !notReady ? guideFor({ mode, order, isStation, action }) : { message: null, target: null };
  // At a packing station, Pack leads with the Now panel (packNowFor) in
  // place of the scan controls, Next line, glow and hand-on button.
  const nowMode = !!order && mode === 'pack' && isStation && canUpdate && !notReady;
  const glowTarget = nowMode ? null : guide.target;
  const goToStage = (stage) => navigate(STAGE_PATHS[stage], { state: { orderType: order.order_type, orderId: order.id } });

  // Suggests the next line worth scanning for Pick/Pack (not Put — that's a
  // correction, not something staff work through a checklist for). Purely
  // a suggestion: doScan resolves whatever barcode is actually scanned
  // regardless of this, so scanning something else, out of order, is
  // always allowed and always works.
  const suggestField = action === 'pack' ? 'qty_packed' : 'qty_picked';
  const orderedLines = inLineOrder(order?.lines || [], lineOrder);
  const nextSuggestedLine = isCorrection(action) ? null : orderedLines.find((l) => outstandingQty(l, suggestField) > 0);

  // The Now panel's job and buttons, and what each button does. Every
  // button is one the page already offered somewhere — the panel just puts
  // the ones that fit the order right now in one place.
  const nextPackLine = orderedLines.find((l) => Number(l.qty_picked || 0) > Number(l.qty_packed || 0));
  // The Packing List leads the Now panel until it's been opened for the
  // packages as they stand (issue #445); putting them in an outer package
  // changes them, so it leads again.
  const packingListKey = order ? `${order.order_type}:${order.id}:${shippablePackages(order.packages).map((p) => p.id).join(',')}` : '';
  const now = nowMode ? packNowFor({ order, action, nextSku: nextPackLine?.product?.sku, packingListPrinted: packingListPrintedFor === packingListKey }) : null;
  const openOrderPackingList = () => {
    setPackingListPrintedFor(packingListKey);
    setPackingList({ orderType: order.order_type, orderId: order.id });
  };
  // Closing a package with something in it opens its label (issue #445):
  // a Parcel label while it's outermost. Printing is optional — closing
  // the preview skips it.
  const labelOnClose = (pkg) => {
    if (isStation && holdsAnything(pkg)) setLabelPackageId(pkg.id);
  };
  const packageAction = async (name, body = {}) => {
    setPackBusy(true);
    try {
      const res = await axios.post(`/api/fulfilment/${order.order_type}/${order.id}/packages/${name}`, body);
      setOrder(res.data.data);
      return res.data.data;
    } catch (err) {
      setScanFeedback({ status: 'error', message: err.response?.data?.message || 'Could not update the package' });
      signalScan('error');
      return null;
    } finally {
      setPackBusy(false);
    }
  };
  const startScan = (key) => {
    ensureAudioContext();
    setAction(key);
    setScanFeedback(null);
    setScanMode('action');
  };
  const onNowAction = (key) => {
    const openPkg = openPackageOf(order.packages);
    if (key === 'scan-pack') startScan('pack');
    else if (key === 'scan-out') startScan('unpack');
    else if (key === 'stop-out') setAction('pack');
    else if (key === 'close-package') packageAction('close').then((next) => { if (next && openPkg) labelOnClose(openPkg); });
    else if (key === 'packing-list') openOrderPackingList();
    else if (key === 'proceed-ship') continueTo('ship');
    else if (key === 'new-package') packageAction('new');
    else if (key.startsWith('into:') && openPkg) packageAction('move', { package_id: Number(key.slice(5)), into_id: openPkg.id });
    else if (key === 'go-pick') goToStage('pick');
    else if (key === 'scan-another') reset();
  };
  // How much of a line is in the open package's own goods — what Take Out
  // can reach.
  const openBoxContents = openPackageOf(order?.packages)?.goods || [];
  const inOpenBox = (l) => Number(openBoxContents.find((c) => String(c.line_id) === String(l.id))?.qty || 0);
  // In the Now layout only the lines there's something to do on get a full
  // card (Take Out: what's in the open package); the rest fold into one row.
  const lineNeedsWork = (l) => {
    if (action === 'unpack') return inOpenBox(l) > 0;
    if (action === 'put') return Number(l.qty_picked || 0) > 0;
    return lineStatusFor(l, mode).tone === 'todo';
  };
  const cardLines = orderedLines.filter(lineNeedsWork);
  const otherLines = orderedLines.filter((l) => !lineNeedsWork(l));
  // The fold's heading: "6 SKUs fully shipped" when they're all done here,
  // else "2 SKUs with nothing to pack".
  const otherLinesLabel = (() => {
    const count = `${otherLines.length} ${otherLines.length === 1 ? 'SKU' : 'SKUs'}`;
    if (!isCorrection(action) && otherLines.every((l) => lineStatusFor(l, mode).tone === 'done')) return `${count} ${FULLY_DONE[mode]}`;
    return `${count} with nothing to ${action === 'unpack' ? 'take out' : action === 'put' ? 'put back' : mode}`;
  })();

  // One SKU's card: what's left to do on it here, its quantities at each
  // stage, and its tap buttons.
  const renderLineCard = (line) => {
    const qtyPicked = Number(line.qty_picked || 0);
    const qtyPacked = Number(line.qty_packed || 0);
    const qtyShipped = Number(line.qty_shipped || 0);
    const qty = Number(line.qty || 0);
    const fmtBreakdown = (rows) => rows.map((r) => `${formatQty(r.qty)} ${r.uomName}`).join(' + ');
    const status = lineStatusFor(line, mode);
    return (
      <div key={line.id} style={{ background: '#fff', border: `1px solid ${status.tone === 'done' ? '#8fd19e' : '#e3e7ee'}`, borderRadius: 8, padding: '8px 10px' }}>
        {/* The SKU, and what's left to do on it on this page in
            plain words ("3 PCK to pick", "Picked") — the one
            thing the card has to answer at a glance. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{line.product?.sku}</span>
          <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap', ...LINE_STATUS_TONES[status.tone] }}>
            {status.text}
          </span>
        </div>
        {/* On Pack the chip says what's left to pack, and once packing
            has started a note says how much is packed of what was
            ordered, in the line's own unit (issue #445) — same as the
            Pick Slip's To Pack. Elsewhere: Ordered, then how much has
            reached each stage, every figure under its own heading. */}
        {mode === 'pack' ? (qtyPacked > 0 && (
          <div style={{ marginTop: 4, fontSize: 12, fontStyle: 'italic', color: '#333' }}>
            Already packed: {formatQty(qtyPacked)} {line.uom?.code || line.uom?.name || ''} of {formatQty(qty - Number(line.qty_cancelled || 0))}
          </div>
        )) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', marginTop: 6, textAlign: 'center', background: '#f7f9fc', borderRadius: 6, padding: '3px 0', lineHeight: 1.3 }}>
          {[
            { label: 'Ordered', qty, rows: () => pickingBreakdown(line), done: false },
            { label: 'Picked', qty: qtyPicked, rows: () => pickedBreakdown(line), done: qtyPicked >= qty },
            { label: 'Packed', qty: qtyPacked, rows: () => packedBreakdown(line), done: qtyPacked >= qty },
            { label: 'Shipped', qty: qtyShipped, rows: () => shippedBreakdown(line), done: qtyShipped >= qty },
          ].map((col, i) => (
            <div key={col.label} style={{ borderLeft: i === 0 ? 'none' : '1px solid #e3e7ee', padding: '0 4px' }}>
              <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 }}>{col.label}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: col.qty > 0 && col.done ? '#1e7e34' : col.qty > 0 ? '#222' : '#aab2bd' }}>
                {col.qty > 0 ? fmtBreakdown(col.rows()) : '—'}
              </div>
            </div>
          ))}
        </div>
        )}
        {line.backordered && (
          <div style={{ marginTop: 4 }}><CategoryPill value="Backordered" /></div>
        )}
        {/* No-scan fallback -- same effect as scanning that unit's
            own barcode, one tap = one unit, same as one scan.
            Named for what it does ("Pick 1 PCK"), so the tap
            reads as recording a pick. Only where this page
            has a scan to record. */}
        {canUpdate && currentAction && tapCandidatesFor(line, action, inOpenBox(line) * soldRateFor(line)).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 6 }}>
          {/* One button per unit, as alternatives — "or" between them,
              so "Pack 1 BOX or Pack 1 PCK" doesn't read as do both. */}
          {tapCandidatesFor(line, action, inOpenBox(line) * soldRateFor(line)).map((c, i) => (
            <React.Fragment key={c.productUomId}>
            {i > 0 && <span style={{ fontSize: 12, color: '#888' }}>or</span>}
            <button
              type="button"
              onClick={() => doTap(c.productUomId)}
              title={`${currentAction.label} one ${c.uomName} of ${line.product?.sku}`}
              style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #1976d2', background: '#eaf2fb', color: '#1976d2', fontSize: 13, fontWeight: 700 }}
            >
              {currentAction.label} 1 {c.uomName}
            </button>
            </React.Fragment>
          ))}
        </div>
        )}
      </div>
    );
  };

  // Built once, rendered in one of two places below: inline in the page
  // flow when the camera isn't open, or overlaid on the camera frame
  // itself while it is (see the scanMode === 'action' block) -- on a phone
  // there's too much above the camera (order card, action selector, this
  // banner, the scan button) for both the banner and the video to be
  // on-screen together otherwise, forcing a scroll between "what's next"
  // and "aim the camera".
  const nextBannerEl = nextSuggestedLine ? (
    <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13, background: '#eef4fb', color: '#1a4971' }}>
      Next: <strong>{nextSuggestedLine.product?.sku}</strong> — {remainingBreakdown(nextSuggestedLine, suggestField).map((r) => `${formatQty(r.qty)} ${r.uomName}`).join(' + ')} remain
    </div>
  ) : (!isCorrection(action) && order?.lines?.length > 0 ? (
    <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13, background: '#e6f4ea', color: '#1e7e34', fontWeight: 600 }}>
      {action === 'pack' ? 'Everything packed' : 'Everything picked'}
    </div>
  ) : null);

  // Same reasoning as nextBannerEl -- overlaid on the camera while it's
  // open rather than left in normal flow, where it scrolled out of view
  // the moment the video appeared.
  const SCAN_FEEDBACK_COLORS = {
    ok: { background: '#e6f4ea', color: '#1e7e34' },
    warn: { background: '#fff4e0', color: '#9a6700' },
    error: { background: '#fef2f2', color: '#b91c1c' },
  };
  const scanFeedbackEl = scanFeedback ? (
    <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, ...SCAN_FEEDBACK_COLORS[scanFeedback.status] }}>
      {scanFeedback.message}
    </div>
  ) : null;

  // The "what's left to scan" queue docked directly under the camera while
  // scanning -- staff who already have the printout don't need the full
  // detailed line cards (breakdowns, tap fallback) on screen at the same
  // time, just SKU + remaining qty, shrinking as each line clears. Put Back
  // has no such queue -- same reasoning as nextSuggestedLine/nextBannerEl,
  // it's a correction against any line, not a checklist to work through.
  const remainingLines = isCorrection(action) ? [] : orderedLines.filter((l) => outstandingQty(l, suggestField) > 0);
  const remainingListEl = !isCorrection(action) ? (
    <div style={{ background: '#fff', border: '1px solid #e3e7ee', borderTop: 'none', borderBottomLeftRadius: 8, borderBottomRightRadius: 8, maxHeight: 160, overflowY: 'auto' }}>
      {remainingLines.length === 0 ? (
        <div style={{ padding: '8px 12px', fontSize: 12, color: '#1e7e34', fontWeight: 600 }}>
          {action === 'pack' ? 'Everything packed' : 'Everything picked'}
        </div>
      ) : remainingLines.map((line) => (
        <div key={line.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '6px 12px', borderBottom: '1px solid #f0f2f5' }}>
          <span style={{ fontWeight: 600 }}>{line.product?.sku}</span>
          <span style={{ color: '#888', whiteSpace: 'nowrap' }}>{remainingBreakdown(line, suggestField).map((r) => `${formatQty(r.qty)} ${r.uomName}`).join(' + ')}</span>
        </div>
      ))}
    </div>
  ) : null;

  // Packages (issues #299, #313) — the open package fills as items are
  // Packed; nothing here has to be done for a one-package order. Hidden while
  // the camera's open, same as the line cards. Changed on Pack only: Ship
  // Order shows them as they are, with a way back to Pack. Above the lines
  // in Pack's Now layout (they're the work there), below them otherwise.
  const packagesPanelEl = order && scanMode !== 'action' && mode !== 'pick' ? (
    <PackagesPanel
      order={order}
      canUpdate={canUpdate && !notReady && isStation && mode === 'pack'}
      canLabel={isStation}
      onChange={(next, change, pkg) => { setOrder(next); if (change === 'close' && pkg) labelOnClose(pkg); }}
      onError={(message) => { setScanFeedback({ status: 'error', message }); signalScan('error'); }}
      onLabel={setLabelPackageId}
      onTakeOut={() => { setAction('unpack'); setScanFeedback(null); }}
      // Not repeated while the Now panel offers it.
      onPackingList={now?.actions.some((a) => a.key === 'packing-list') ? null : openOrderPackingList}
      highlightId={highlightPackageId}
      guideTarget={glowTarget}
      closeInNowPanel={nowMode}
    />
  ) : null;

  return (
    // width:100% is explicit rather than relying on the flex parent's
    // align-items:stretch to fill it -- once this container holds nested
    // flex rows of its own (the action selector, the line-card list), some
    // Chromium/Android WebView builds shrink a width:auto flex child to fit
    // its content instead of stretching it, which is why the shallow
    // pre-Order screen (a single flex row) rendered full width while the
    // Order-loaded screen (several nested flex rows/columns) rendered
    // visibly narrower.
    <div style={{ width: '100%', maxWidth: 640, margin: '0 auto', padding: 16 }}>
      <h2 style={{ margin: '8px 0 16px', fontSize: 20 }}>{stagePage.title}</h2>

      {!order && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              className="styled-input"
              type="text"
              placeholder="Order No. e.g. SO-00001"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={() => manualValue.trim() && lookup(manualValue.trim())}
              disabled={loading || !manualValue.trim()}
              style={{ padding: '0 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}
            >
              Find
            </button>
          </div>

          <button
            type="button"
            onClick={() => { ensureAudioContext(); setScanMode((m) => (m === 'order' ? null : 'order')); }}
            style={{ width: '100%', padding: '12px 0', background: scanMode === 'order' ? '#eceef1' : '#1976d2', color: scanMode === 'order' ? '#333' : '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15 }}
          >
            {scanMode === 'order' ? 'Stop Scanning' : 'Scan Barcode'}
          </button>

          {scanMode === 'order' && <div id={SCANNER_ELEMENT_ID} style={{ marginTop: 12, borderRadius: 8, width: '100%' }} />}

          {loading && <div style={{ marginTop: 12, color: '#888' }}>Looking up...</div>}
          {error && <div style={{ marginTop: 12, color: '#b91c1c' }}>{error}</div>}

          {openOrders.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {stagePage.listTitle}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {openOrders.map((o) => (
                  <button
                    key={`${o.order_type}-${o.id}`}
                    type="button"
                    onClick={() => selectOrder(o.order_type, o.id)}
                    style={{ width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #e3e7ee', borderRadius: 8, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{o.display_number}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{o.type_label} · {o.party?.name}</div>
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                      <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>{stagePage.progress(o)}</span>
                      {/* On Ship: whether this is the whole order (or the
                          rest of it) or only part. */}
                      {mode === 'ship' && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap', ...(o.whole_ready ? LINE_STATUS_TONES.done : LINE_STATUS_TONES.waiting) }}>
                          {o.whole_ready ? (Number(o.qty_shipped_total) > 0 ? 'Rest of order ready' : 'Whole order ready') : 'Part ready'}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {openLoaded && openOrders.length === 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ padding: '12px 14px', borderRadius: 8, background: '#f3f5f8', color: '#5a6b85', fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
                {NOTHING_READY[mode]}
              </div>
              {earlierOrders.map((group) => (
                <div key={group.stage} style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {STAGE_PAGES[group.stage].listTitle}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {group.orders.map((o) => (
                      <button
                        key={`${o.order_type}-${o.id}`}
                        type="button"
                        onClick={() => navigate(STAGE_PATHS[group.stage], { state: { orderType: o.order_type, orderId: o.id } })}
                        title={`Open on ${STAGE_PAGES[group.stage].title}`}
                        style={{ width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #e3e7ee', borderRadius: 8, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                      >
                        <span>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{o.display_number}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>{o.type_label} · {o.party?.name}</div>
                        </span>
                        <span style={{ fontSize: 12, color: '#1976d2', fontWeight: 600, whiteSpace: 'nowrap' }}>{STAGE_PAGES[group.stage].progress(o)} →</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {order && (
        <>
          {/* Collapses to one compact line while the camera's open -- staff
              already know which Order they're on by then, and every bit of
              chrome above the camera is space the camera itself doesn't get. */}
          {scanMode === 'action' || nowMode ? (
            <div style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, padding: '8px 14px', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, fontSize: 13 }}>
              {scanMode !== 'action'
                ? <RecordChip label={order.display_number} href={order.link} title={`Open ${order.type_label} ${order.display_number}`} size="sm" />
                : <span style={{ fontWeight: 700 }}>{order.display_number}</span>}
              <span style={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'right' }}>{order.party?.name}</span>
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>{order.type_label}</div>
                  {/* The order number opens the order's own page, closing
                      the loop with its "Pick / Pack" deep link. */}
                  <RecordChip label={order.display_number} href={order.link} title={`Open ${order.type_label} ${order.display_number}`} />
                </div>
              </div>
              <div style={{ color: '#666', fontSize: 13, marginTop: 2 }}>{order.party?.role}: {order.party?.name}</div>
              <div style={{ color: '#666', fontSize: 13 }}>{order.status_label}</div>
            </div>
          )}

          {/* Soft warning, not a hard block — scanning still works while an
              order's on hold (see fulfilmentService.scan), this
              just makes sure staff can't miss that it is. Shown in both
              header states (even the compact one while the camera's open)
              since it's safety info, not chrome. */}
          {order.on_hold && (
            <div style={{ background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: '#7a5c00' }}>
              <strong>On Hold</strong>{order.on_hold_reason ? ` — ${order.on_hold_reason}` : ''}
            </div>
          )}

          {/* An order not yet open to picking (a Draft or Submitted Sales
              Order) can't be picked, packed or shipped — every scan is
              refused server-side; this says why up front. */}
          {notReady && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: '#b91c1c' }}>
              <strong>{order.not_ready?.title}</strong> — {order.not_ready?.detail}
            </div>
          )}

          {/* Where the order's up to, and what to do next — hidden while
              the camera's open, like the rest of the chrome. */}
          {scanMode !== 'action' && (
            <>
              <FulfilmentStepper order={order} mode={mode} target={glowTarget} onGo={goToStage} />
              {nowMode && <NowPanel now={now} onAction={onNowAction} busy={packBusy} />}
            </>
          )}

          {/* Order-level Ship (issue #205) — replaces the old per-line
              "Scan to Ship" action removed from ACTIONS above. On Ship Order
              only (issue #306), while the camera isn't open (same "collapse
              chrome around the camera" rule the rest of this page follows)
              and only when there's actually something to ship. */}
          {/* Always there on Ship, so an order with no package ready says
              so plainly instead of the button vanishing. */}
          {canUpdate && mode === 'ship' && !notReady && scanMode !== 'action' && (
            <button
              type="button"
              onClick={() => setShipDialogOpen(true)}
              disabled={!canShipPackage || !!order.ship_blocked}
              className={canShipPackage && !order.ship_blocked ? glowClass(glowTarget, 'ship') : undefined}
              style={{ width: '100%', padding: '12px 0', marginBottom: order.ship_blocked ? 6 : 12, background: canShipPackage && !order.ship_blocked ? '#1e7e34' : '#c7ccd4', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: canShipPackage && !order.ship_blocked ? 'pointer' : 'not-allowed' }}
            >
              {canShipPackage ? 'Ship Order' : fullyShipped ? 'Fully Shipped' : 'Nothing to Ship'}
            </button>
          )}
          {/* An unpaid Prepay order (issue #338) says why it can't ship
              before anything is filled in. */}
          {canUpdate && mode === 'ship' && !notReady && scanMode !== 'action' && canShipPackage && order.ship_blocked && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#b91c1c' }}>
              {order.ship_blocked.message}
            </div>
          )}

          {/* Picking, packing and putting back change the order, so a
              staff member who can only read this screen gets the order
              view without them (issue #252). */}
          {!canUpdate && (
            <div style={{ background: '#eef4fb', border: '1px solid #cfe0f3', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#1e3a5f' }}>
              Your roles let you view this order but not pick, pack, put back or ship it.
            </div>
          )}
          {/* Packing, packages and labels happen at a packing
              station (issue #305) — a PC with its label printers linked. */}
          {canUpdate && !isStation && mode === 'pack' && scanMode !== 'action' && (
            <div style={{ background: '#eef4fb', border: '1px solid #cfe0f3', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#1e3a5f' }}>
              Packing is done at a packing station. To pack on this PC,{' '}
              <button type="button" onClick={() => navigate('/warehouse-packing-station')} style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>
                set it up as a packing station
              </button>.
            </div>
          )}
          {/* In the Now layout the panel holds the scan buttons; the camera
              view keeps its own controls (switch Pack/Put Back mid-scan, Stop). */}
          {canUpdate && ACTIONS.length > 0 && (!nowMode || scanMode === 'action') && (
          <>
          {/* Action selector — governs what the next scan does. Changing it
              mid-scan is fine; it only affects future scans. */}
          <div style={{ display: 'flex', border: '1px solid #bfc8d6', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
            {ACTIONS.map((a) => (
              <button
                key={a.key}
                type="button"
                className={glowClass(glowTarget, `action-${a.key}`, { inset: true })}
                onClick={() => { setAction(a.key); setScanFeedback(null); }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  border: 'none',
                  borderLeft: a.key === ACTIONS[0].key ? 'none' : '1px solid #bfc8d6',
                  background: action === a.key ? '#1976d2' : '#fff',
                  color: action === a.key ? '#fff' : '#333',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {a.label}
              </button>
            ))}
          </div>

          {/* A suggestion, not a requirement — doScan resolves whatever
              barcode actually gets scanned regardless of this, so scanning
              a different line, out of order, always works. This just saves
              hunting through the list below on a small screen. Only shown
              here (in normal page flow) while the camera is closed — once
              it's open, the same banner moves onto the camera frame below. */}
          {scanMode !== 'action' && nextBannerEl && <div style={{ marginBottom: 10 }}>{nextBannerEl}</div>}

          <button
            type="button"
            onClick={() => { ensureAudioContext(); setScanFeedback(null); setScanMode((m) => (m === 'action' ? null : 'action')); }}
            style={{ width: '100%', padding: '12px 0', marginBottom: 12, background: scanMode === 'action' ? '#eceef1' : '#1976d2', color: scanMode === 'action' ? '#333' : '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15 }}
          >
            {scanMode === 'action' ? 'Stop Scanning' : currentAction.scanLabel}
          </button>
          </>
          )}

          {scanMode !== 'action' && scanFeedbackEl && <div style={{ marginBottom: 12 }}>{scanFeedbackEl}</div>}

          {scanMode === 'action' && (
            <>
              {/* Wrapper only positions overlays around the camera div — it
                  never applies size/overflow constraints to the camera div
                  itself, which previously broke html5-qrcode's own
                  corner-marker overlay and decode region (see the scanner
                  effect above). Both banners stack at the top, not
                  top+bottom — a bottom overlay ran off the bottom of the
                  screen on a phone. The remaining-queue list is a plain
                  sibling docked directly under the camera, not another
                  overlay on top of it, for the same reason: it's a scrolling
                  list of several rows, not one line to glance at, and
                  laying it over the video would make the barcode itself
                  harder to see (even though it wouldn't affect decoding,
                  which reads the camera's raw frames, not what's drawn over
                  them on screen). */}
              <div style={{ position: 'relative' }}>
                {(nextBannerEl || scanFeedbackEl) && (
                  <div style={{ position: 'absolute', top: 6, left: 6, right: 6, zIndex: 2, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {nextBannerEl && (
                      <div style={{ borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.35)' }}>
                        {nextBannerEl}
                      </div>
                    )}
                    {scanFeedbackEl && (
                      <div style={{ borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.35)' }}>
                        {scanFeedbackEl}
                      </div>
                    )}
                  </div>
                )}
                <div id={SCANNER_ELEMENT_ID} style={{ borderRadius: remainingListEl ? '8px 8px 0 0' : 8, width: '100%' }} />
              </div>
              {remainingListEl}
            </>
          )}

          {/* Full detail (breakdowns, tap-to-correct fallback) only when the
              camera's closed -- while scanning, remainingListEl above covers
              "what's left" in the space the camera doesn't need, and this
              much more detailed view would just push the camera further
              down the page for no reason at that point. */}
          {scanMode !== 'action' && (
            <div style={{ marginTop: 12 }}>
              {nowMode && packagesPanelEl}
              {/* The order's SKUs as one group under its own heading, like
                  Packages, with the SKU / Line switch on that row. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: nowMode && packagesPanelEl ? 16 : 4, marginBottom: 6 }}>
                {/* Not capitalised: "SKUS" reads as a word, not SKU's plural. */}
                <span style={{ fontSize: 12, color: '#888', fontWeight: 600, letterSpacing: 0.4 }}>SKUs</span>
                {orderedLines.length > 1 && <LineOrderSwitch value={lineOrder} onChange={setLineOrder} />}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cardLines.map(renderLineCard)}
              {otherLines.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e3e7ee', borderRadius: 8 }}>
                  <button
                    type="button"
                    onClick={() => setShowOtherLines((v) => !v)}
                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#333', cursor: 'pointer' }}
                  >
                    {showOtherLines ? '▾' : '▸'} {otherLinesLabel}
                  </button>
                  {showOtherLines && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 8px 8px' }}>
                      {otherLines.map(renderLineCard)}
                    </div>
                  )}
                </div>
              )}
              {(order.lines || []).length === 0 && <div style={{ color: '#888' }}>No SKUs on this Order.</div>}
              </div>
            </div>
          )}

          {/* Packages below the lines, outside the Now layout (see
              packagesPanelEl). */}
          {!nowMode && packagesPanelEl}
          {scanMode !== 'action' && mode === 'ship' && canUpdate && !notReady && isStation && (order.packages || []).length > 0 && (
            <button type="button" onClick={() => continueTo('pack')} style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>
              Change packages on Pack

            </button>
          )}
          {/* What's shipped so far, to check, correct or reverse (see
              ShippedPanel.js) — Pack and Ship only. */}
          {scanMode !== 'action' && mode !== 'pick' && (
            <ShippedPanel
              order={order}
              refreshKey={(order.lines || []).map((l) => l.qty_shipped).join(',')}
              canUpdate={canUpdate}
              onLabel={isStation ? setLabelPackageId : null}
              onSenderLabels={isStation ? setSenderLabelsShipmentId : null}
              onPackingList={(id) => setPackingList({ shipmentId: id })}
              onReversed={() => selectOrder(order.order_type, order.id)}
            />
          )}

          {/* Same Pick Slip as the order's own Print Pick Slip action (e.g.
              the Sales Order screen's), offered from the same point on. On
              Pick only (issue #445): each page offers its own document, and
              Pack's is the Packing List. */}
          {mode === 'pick' && (
          <button
            type="button"
            onClick={() => setShowPickSlip(true)}
            disabled={notReady}
            title={notReady ? order.not_ready?.detail : undefined}
            style={{ width: '100%', marginTop: 16, padding: '12px 0', background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 8, fontWeight: 600, cursor: notReady ? 'not-allowed' : 'pointer', opacity: notReady ? 0.5 : 1 }}
          >
            Print Pick Slip
          </button>
          )}

          <button
            type="button"
            onClick={reset}
            className={glowClass(glowTarget, 'scan-another')}
            style={{ width: '100%', marginTop: 10, padding: '12px 0', background: '#eceef1', color: '#333', border: 'none', borderRadius: 8, fontWeight: 600 }}
          >
            Scan Another Order
          </button>

          {showPickSlip && (
            <ReportOverlay onClose={() => setShowPickSlip(false)}>
              <PickSlipReportView orderType={order.order_type} orderId={order.id} onClose={() => setShowPickSlip(false)} />
            </ReportOverlay>
          )}

          {packingList && (
            <ReportOverlay onClose={() => setPackingList(null)}>
              <PackingListReportView shipmentId={packingList.shipmentId} orderType={packingList.orderType} orderId={packingList.orderId} onClose={() => setPackingList(null)} />
            </ReportOverlay>
          )}

          {labelPackageId && (
            <ReportOverlay onClose={() => setLabelPackageId(null)}>
              <BoxLabelView packageId={labelPackageId} onClose={() => setLabelPackageId(null)} />
            </ReportOverlay>
          )}

          {senderLabelsShipmentId && (
            <ReportOverlay onClose={() => setSenderLabelsShipmentId(null)}>
              <SenderLabelsView shipmentId={senderLabelsShipmentId} onClose={() => setSenderLabelsShipmentId(null)} />
            </ReportOverlay>
          )}

          {shipDialogOpen && (
            <ShipConfirmDialog
              open
              onOpenChange={(next) => { if (!next) setShipDialogOpen(false); }}
              lines={order.lines}
              packages={order.packages}
              order={order}
              orderDate={order?.order_date}
              shipping={shipping}
              onConfirm={handleConfirmShip}
            />
          )}
        </>
      )}
    </div>
  );
}

export default PickOrderPage;
